import { BookmarksContext } from "context/bookmarks";
import React, { memo, useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import Loader from "components/loader";
import Typography from "@material-ui/core/Typography";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import Button from "@material-ui/core/Button";
import Box from "@material-ui/core/Box";
import formatDate from "date-fns/format";
import TabPanel from "components/common/tab-panel";
import MoreIcon from "@material-ui/icons/MoreHoriz";
import DropdownMenu from "components/common/dropdown-menu";
import { queuePropType, readerRefPropType } from "utils/prop-types";
import CloseIcon from "@material-ui/icons/Close";
import EditIcon from "components/icons/edit";
import clsx from "clsx";
import BookmarkDialog from "components/ebook-reader/components/navigation-menu/bookmark-dialog.component";
import { connect } from "react-redux";
import { NAVIGATION_TABS } from "components/ebook-reader/ebook-reader.utils";


const MAX_DESCRIPTION_TEXT_LENGTH = 400;

const locationToBookmark = (item) => ({
  id: item?.id,
  page: item?.locations?.position,
  title: item?.name,
  note: item?.note,
  date: item?.created,
  selector: item,
});


const BookmarksTabPanel = memo((props) => {
    const { queue, activeIndex, activeTab, readerRef, handleNavItemClick, classes } = props;
    const { assetId } = queue[activeIndex] ?? {};
    const { t } = useTranslation();
    const tab = useMemo(() => NAVIGATION_TABS.bookmarks, []);

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [currentBookmark, setCurrentBookmark] = useState();
    const { updateBookmark: updateBookmarkLocation, bookmarks, deleteBookmark, isLoading } = useContext(BookmarksContext);

    const items = useMemo(() => {
        return bookmarks.map(locationToBookmark).sort((a, b) => a.page - b.page)
    }, [bookmarks])

    const closeDialog = useCallback(() => {
        setIsDialogOpen(false);
        setCurrentBookmark(undefined);
    }, [setIsDialogOpen, setCurrentBookmark])

    const openDialog = useCallback((bookmark) => (e) => {
        setCurrentBookmark(bookmark)
        setIsDialogOpen(true)
        e.stopPropagation();
    }, []);

    const updateBookmark = useCallback((bookmark, title, note) => async () => {
        await updateBookmarkLocation(assetId, bookmark.selector, title, note);
        closeDialog();
    }, [assetId]);

    const onDeleteClick = useCallback((location) => (e) => {
        e.stopPropagation();
        return deleteBookmark(assetId, location);
    }, [assetId, deleteBookmark]);

    const exportBookmarks = useCallback(() => {
        setIsExporting(true);
        const textBookmarks = items.map(item => {
            return [
                ' ',
                item.title || t('generic_bookmark'),
                item.note,
                item.date && formatDate(new Date(item.date), 'd MMMM yyyy'),
                item.selector.title,  // chapter title
                t('generic_format_page', { arg: item.page }),
                ' '
            ]
            .filter(row => !!row)
            .join('\n');
        });
        const blob = new Blob(textBookmarks, {type: "text/plain"});
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.download = `${t("ebook_navigation_section_bookmarks").toLowerCase()}.txt`;
        link.href = url;
        link.click();
        setTimeout(() => setIsExporting(false), 500);
    }, [items]);

    return (
        <TabPanel key={tab} value={activeTab} index={tab} className={clsx(classes.tabPanel, classes.bookmarkTabPanel)}>
            {isLoading && <Loader />}
            {!items?.length ? (
              <>
                <Typography noWrap className={classes.emptyMessageTitle}>
                  {t('ebook_no_bookmarks_title')}
                </Typography>
                <Typography noWrap className={classes.emptyMessageSubtitle}>
                  {t('ebook_no_bookmarks_subtitle')}
                </Typography>
              </>
            ) : (
            <>
            <List className={classes.listContainer}>
                {items.map((item) => (
                <ListItem
                    button
                    key={item.id}
                    className={classes.navItem}
                    onClick={handleNavItemClick(item.selector)}
                  >
                    <Box className={classes.navItemMetadata}>
                        <Typography className={classes.navItemSubTitle}>
                            {!!item?.date && formatDate(new Date(item.date), 'd MMMM yyyy')}
                        </Typography>
                        <DropdownMenu
                          readerRef={readerRef}
                          icon={MoreIcon}
                          controlBtnProps={{
                            className: classes.optionsButton,
                            iconClassName: classes.optionsIcon,
                          }}
                          popperClassName={classes.popper}
                          items={[
                              {
                                  label: t("ebook_bookmarks_button_edit"),
                                  icon: EditIcon,
                                  onClick: openDialog(item),
                                  closeMenuAfterAction: true,
                                  className: classes.dropdownItem,
                                  iconPlacement: 'left',
                                  labelClassName: classes.dropdownLabel,
                                  iconProps: {
                                      className: classes.dropdownIcon,
                                  }
                              },
                              {
                                  label: t("ebook_bookmarks_button_delete"),
                                  icon: CloseIcon,
                                  onClick: onDeleteClick(item.selector),
                                  closeMenuAfterAction: true,
                                  className: clsx(classes.dropdownItem, classes.deleteDropdownItem),
                                  iconPlacement: 'left',
                                  labelClassName: classes.dropdownLabel,
                                  iconProps: {
                                      className: classes.dropdownIcon,
                                  }
                              }
                          ]}
                        />
                    </Box>
                    <Box className={classes.navItemMetadata}>
                        <Typography className={classes.navItemTitle}>
                          {item.title || t('generic_bookmark')}
                        </Typography>
                        {!!item?.page && (
                          <Typography className={classes.navItemSubTitle}>
                            {t('generic_format_page', { arg: item.page })}
                          </Typography>
                        )}
                    </Box>
                    {!!item?.note && (
                      <Typography className={classes.navItemSubTitle}>
                        "
                        {item.note.length > MAX_DESCRIPTION_TEXT_LENGTH
                          ? `${item.note.substring(0, MAX_DESCRIPTION_TEXT_LENGTH)}...`
                          : item.note}
                        "
                      </Typography>
                    )}
                  </ListItem>
                ))}
            </List>
            </>
            )}
            {!!items?.length && <>
            <div className={classes.bookmarkPanelBottom}>
                <Button
                    variant="outlined"
                    disabled={isExporting}
                    onClick={exportBookmarks}
                    className={classes.exportBookmarks}
                >
                    {t`ebook_bookmarks_button_export`}
                    {isExporting && <Loader />}
                </Button>
            </div>
            <BookmarkDialog
                isOpen={isDialogOpen}
                bookmark={currentBookmark}
                onSave={updateBookmark}
                onClose={closeDialog}
            />
            </>}
        </TabPanel>
    )
});

BookmarksTabPanel.propTypes = {
    queue: queuePropType,
    activeIndex: PropTypes.number.isRequired,
    activeTab: PropTypes.oneOf(Object.keys(NAVIGATION_TABS)).isRequired,
    handleNavItemClick: PropTypes.func.isRequired,
    readerRef: readerRefPropType,
    classes: PropTypes.object.isRequired,
}

const mapStateToProps = ({ player }) => ({
  queue: player.queue,
  activeIndex: player.activeIndex,
});

export default connect(mapStateToProps)(BookmarksTabPanel);
